import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  Alert,
  CircularProgress,
  Typography,
  Grid
} from '@mui/material';
import { SchedulerConfigFormData, SchedulerConfig } from '@/types/smsScheduler';
import { formatErrorForDisplay } from '@/utils/errorUtils';

interface SchedulerConfigFormProps {
  initialData?: SchedulerConfig | null;
  onSubmit: (data: SchedulerConfigFormData) => void;
  onCancel: () => void;
  loading?: boolean;
  error?: string | null;
  mode: 'create' | 'edit';
}

const SchedulerConfigForm: React.FC<SchedulerConfigFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  loading = false,
  error = null,
  mode
}) => {
  const [formData, setFormData] = useState<SchedulerConfigFormData>({
    schedule_name: '',
    days_before_red: 30,
    time_of_day: '10:00:00',
    is_active: true
  });

  const [formErrors, setFormErrors] = useState<Partial<SchedulerConfigFormData>>({});

  useEffect(() => {
    if (initialData && mode === 'edit') {
      setFormData({
        schedule_name: initialData.schedule_name,
        days_before_red: initialData.days_before_red,
        time_of_day: initialData.time_of_day,
        is_active: initialData.is_active
      });
    }
  }, [initialData, mode]);

  const validateForm = (): boolean => {
    const errors: any = {};

    if (!formData.schedule_name.trim()) {
      errors.schedule_name = 'Schedule name is required';
    } else if (formData.schedule_name.length < 3) {
      errors.schedule_name = 'Schedule name must be at least 3 characters';
    }

    if (formData.days_before_red < 1 || formData.days_before_red > 365) {
      errors.days_before_red = 'Days before RED must be between 1 and 365';
    }

    if (!formData.time_of_day) {
      errors.time_of_day = 'Time of day is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field: keyof SchedulerConfigFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.type === 'checkbox' 
      ? event.target.checked 
      : event.target.value;
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {formatErrorForDisplay(error)}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            label="Schedule Name"
            value={formData.schedule_name}
            onChange={handleInputChange('schedule_name')}
            fullWidth
            required
            error={!!formErrors.schedule_name}
            helperText={formErrors.schedule_name || 'Unique name for this scheduler configuration'}
            placeholder="e.g., 30_days_before_renewal"
            disabled={loading}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label="Days Before RED"
            type="number"
            value={formData.days_before_red}
            onChange={handleInputChange('days_before_red')}
            fullWidth
            required
            error={!!formErrors.days_before_red}
            helperText={formErrors.days_before_red || 'Number of days before RED to trigger SMS (1-365)'}
            inputProps={{ min: 1, max: 365 }}
            disabled={loading}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label="Time of Day"
            type="time"
            value={formData.time_of_day}
            onChange={handleInputChange('time_of_day')}
            fullWidth
            required
            error={!!formErrors.time_of_day}
            helperText={formErrors.time_of_day || 'Time to execute the scheduler (IST)'}
            InputLabelProps={{ shrink: true }}
            disabled={loading}
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_active}
                onChange={handleInputChange('is_active')}
                disabled={loading}
              />
            }
            label={
              <Box>
                <Typography variant="body2">
                  Active Scheduler
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formData.is_active 
                    ? 'Scheduler will run automatically at the specified time'
                    : 'Scheduler is disabled and will not run automatically'
                  }
                </Typography>
              </Box>
            }
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Saving...' : mode === 'create' ? 'Create Scheduler' : 'Update Scheduler'}
        </Button>
      </Box>
    </Box>
  );
};

export default SchedulerConfigForm;
